import { DateTime } from "luxon";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import EmptyState from "../../Shared/EmptyState";
import { useUserPreferences } from "../../../hooks/useUserPreferences";

// Builds the leading stacked date token — month in smallcaps above the
// day-of-month in font-display. Always a single day (the start day) so the
// fixed-width token never wraps and every card stays the same height; the
// full date/range reads horizontally in the heading line instead.
const buildDateToken = (start) => ({
  month: start.toFormat("LLL").toUpperCase(),
  day: String(start.day),
});

// Primary heading — the human date, single day or range, read horizontally
// using the app's dominant "MMM d" Luxon convention with an en-dash.
const buildDateLine = (start, end, allDay) => {
  const isRange = allDay && !start.hasSame(end, "day");
  if (!isRange) return start.toFormat("EEE, MMM d");
  return start.hasSame(end, "month")
    ? `${start.toFormat("MMM d")}–${end.toFormat("d")}`
    : `${start.toFormat("MMM d")} – ${end.toFormat("MMM d")}`;
};

// Secondary meta line — "All day" (+ "· N days" for a range) or the time
// window, respecting 12h/24h prefs.
const buildMetaLine = (start, end, allDay, is12HourClock) => {
  if (!allDay) {
    const timeFormat = is12HourClock ? "h:mm a" : "HH:mm";
    return `${start.toFormat(timeFormat)}–${end.toFormat(timeFormat)}`;
  }
  const days = Math.round(end.endOf("day").diff(start.startOf("day"), "days").days);
  return days > 1 ? `All day · ${days} days` : "All day";
};

const TimeOffCard = ({ block, is12HourClock, onEdit, onDelete }) => {
  const start = DateTime.fromISO(block.startTime, { zone: block.timeZoneId });
  const end = DateTime.fromISO(block.endTime, { zone: block.timeZoneId });
  const dateToken = buildDateToken(start);
  const dateLine = buildDateLine(start, end, block.allDay);
  const metaLine = buildMetaLine(start, end, block.allDay, is12HourClock);

  return (
    <li className="flex items-center gap-4 rounded-card border border-ink-100 bg-white p-4 shadow-hairline">
      <div className="flex w-14 flex-none flex-col items-center text-center">
        <span className="smallcaps text-ink-400">{dateToken.month}</span>
        <span className="font-display text-2xl leading-tight text-ink-900">
          {dateToken.day}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-medium text-ink-900">{dateLine}</p>
          {block.overlappingAppointmentCount > 0 && (
            <span className="inline-flex items-center rounded-pill bg-signal-amber-50 px-2 py-0.5 text-xs font-medium text-signal-amber-600 ring-1 ring-inset ring-signal-amber-100">
              {block.overlappingAppointmentCount} booked
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-sm text-ink-700">
          {metaLine}
          {block.reason ? ` · ${block.reason}` : ""}
        </p>
      </div>

      <div className="flex flex-none items-center gap-1">
        <button
          type="button"
          onClick={() => onEdit(block)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-pill px-2.5 text-ink-500 hover:bg-cream-100 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/30 sm:px-3"
        >
          <PencilSquareIcon aria-hidden="true" className="size-5" />
          <span className="hidden text-sm font-medium sm:inline">Edit</span>
          <span className="sr-only sm:hidden">Edit time off</span>
        </button>
        <button
          type="button"
          onClick={() => onDelete(block)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-pill px-2.5 text-ink-500 hover:bg-signal-red-50 hover:text-signal-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-red-500/30 sm:px-3"
        >
          <TrashIcon aria-hidden="true" className="size-5" />
          <span className="hidden text-sm font-medium sm:inline">Delete</span>
          <span className="sr-only sm:hidden">Delete time off</span>
        </button>
      </div>
    </li>
  );
};

const TimeOffList = ({ blocks, onEdit, onDelete, onAdd }) => {
  const { is12HourClock } = useUserPreferences();

  const sortedBlocks = [...(blocks || [])].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime)
  );

  if (sortedBlocks.length === 0) {
    return (
      <EmptyState
        text="No time off scheduled"
        subText="Block out vacations, appointments, or breaks so clients can't book them."
        buttonText="Add time off"
        onClick={onAdd}
      />
    );
  }

  return (
    <ul className="space-y-3">
      {sortedBlocks.map((block) => (
        <TimeOffCard
          key={block._id}
          block={block}
          is12HourClock={is12HourClock}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
};

export default TimeOffList;
